import { useEffect, useMemo, useState } from 'react';

import { toZonedTime } from 'date-fns-tz';
import { addYears, subYears } from 'date-fns';

import { cn } from 'lib/helpers/utility';
import useTimezone from 'lib/hooks/useTimezone';
import { parseDateOnly } from 'lib/helpers/dateUtils';

import Popover from 'components/shared/Popovers/Popover/Popover';

import Header from '../shared/Header';
import Calendar from '../Calendar/Calendar';

import Footer from './components/Footer';
import OuterButton from './components/OuterButton';
import { getRangeError } from './lib/getRangeError';
import { combineDateTime } from './lib/combineDateTime';
import DateSelector from './components/DateSelector/DateSelector';
import TimeSelector from './components/TimeSelector/TimeSelector';
import { getButtonText, getDisplayFormat } from './lib/getDateFormats';
import { extractDateValue, extractTimeValue } from './lib/extractValues';

const toDateOnlyString = (date) => {
  if (!date) return null;
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const y = date.getFullYear();
  return `${m}/${d}/${y}`;
};

/**
 * @param {import('./lib/propTypes').DatePickerProps} props
 */

const Datepicker = (props) => {
  const {
    value = null,
    onChange = () => {},
    format = 'MM/DD/YYYY',
    isDate = true,
    isDateOnly = false,
    isDateRange = false,
    isTime = false,
    isTimeRange = false,
    isTimezone: isTimezoneProp,
    buttonIcon = 'calendar-mono',
    buttonIconSize = 20,
    buttonIconClassName,
    buttonText,
    buttonTextClassName = '',
    buttonClassName = '',
    parentClassName = '',
    label = '',
    required = false,
    error = null,
    customButtonTrigger = null,
    showTimeRightComponent = true,
    minDate = subYears(new Date(), 150),
    maxDate = addYears(new Date(), 150)
  } = props;

  const timezone = useTimezone();
  const isTimezoneMode = isTimezoneProp ?? (isTime || isTimeRange);
  const effectiveTimezone = isTimezoneMode ? timezone : null;

  // Single source of truth for the picker's state
  const [internalValue, setInternalValue] = useState(value);

  // Sync with external value changes
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Derive date and time portions from internal value
  const dateValue = useMemo(
    () => extractDateValue(internalValue, isDateRange),
    [internalValue, isDateRange]
  );

  const timeValue = useMemo(
    () => extractTimeValue(internalValue, isTimeRange),
    [internalValue, isTimeRange]
  );

  const displayFormat = getDisplayFormat(format, value, isTime, isTimeRange);
  const buttonDisplayText = getButtonText({
    value,
    baseFormat: format,
    buttonText,
    isDate,
    isDateRange,
    isTime,
    isTimeRange,
    timezone: effectiveTimezone
  });

  // Simplified handlers - only update internalValue
  const handleDateChange = (newDateValue) => {
    const combined = combineDateTime(
      newDateValue,
      timeValue,
      isDateRange,
      isTimeRange,
      effectiveTimezone
    );
    setInternalValue(combined);
  };

  const handleTimeChange = (newTimeValue) => {
    // For time range without date range, ensure we have a date (use today if none selected)
    const effectiveDateValue = dateValue || (isTimeRange && !isDateRange ? new Date() : null);
    const combined = combineDateTime(
      effectiveDateValue,
      newTimeValue,
      isDateRange,
      isTimeRange,
      effectiveTimezone
    );
    setInternalValue(combined);
  };

  const handleCalendarChange = (calendarValue) => {
    const combined = combineDateTime(
      calendarValue,
      timeValue,
      isDateRange,
      isTimeRange,
      effectiveTimezone
    );
    setInternalValue(combined);
  };

  const isRange = isDateRange || isTimeRange;

  const rangeError = useMemo(
    () => getRangeError(internalValue, isRange, effectiveTimezone),
    [internalValue, isRange, effectiveTimezone]
  );

  const isValid = Boolean(
    (Array.isArray(internalValue) ? internalValue[0] && internalValue[1] : internalValue) &&
    !rangeError
  );

  // Convert internal value to the output format expected by the consumer
  const convertOutput = (val) => {
    if (!val) return null;

    if (isDateOnly) {
      // Date-only strings pass through directly
      const dateOnly = typeof val === 'string' ? parseDateOnly(val) : null;
      if (dateOnly) return toDateOnlyString(dateOnly);

      // UTC ISO string: convert to timezone to get the correct calendar date
      if (typeof val === 'string' && /T/.test(val)) {
        const zoned = toZonedTime(new Date(val), timezone);
        return toDateOnlyString(zoned);
      }

      // Date object: use local fields directly
      if (val instanceof Date) return toDateOnlyString(val);

      return toDateOnlyString(new Date(val));
    }

    if (isTimezoneMode) {
      // Return UTC ISO string
      if (typeof val === 'string' && /T/.test(val)) return val;
      if (val instanceof Date) return val.toISOString();
      return new Date(val).toISOString();
    }

    // Default: return as-is for backward compat
    return val;
  };

  const onApply = (closePopover) => {
    if (!isValid) return;

    let output;
    if (isDateRange || isTimeRange) {
      output = Array.isArray(internalValue) ? internalValue.map(convertOutput) : internalValue;
    } else {
      output = convertOutput(internalValue);
    }

    onChange(output);
    closePopover();
  };

  const onCancel = (closePopover) => {
    // Reset to original value
    setInternalValue(value);
    closePopover();
  };

  const handleKeyDown = (event, closePopover) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (isValid) onApply(closePopover);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onCancel(closePopover);
    }
  };

  const showDateSelector = isDate || isDateRange;
  const showTimeSelector = isTime || isTimeRange;
  const calendarValue = dateValue || internalValue;

  return (
    <div className={cn('w-full', parentClassName)}>
      <Header label={label} required={required} />
      <Popover
        isFixed
        className="w-full"
        panelClassName="p-0 w-[368px] rounded-2xl border border-solid border-neutral-200 bg-white p-0 shadow-[0px_0px_20px_0px_rgba(55,65,69,0.08)]"
        customButtonClassName="w-full"
        customButtonTrigger={
          customButtonTrigger || (
            <OuterButton
              icon={buttonIcon}
              iconSize={buttonIconSize}
              iconClassName={buttonIconClassName}
              text={buttonDisplayText}
              textClassName={buttonTextClassName}
              className={buttonClassName}
              value={value}
            />
          )
        }>
        {({ closePopover }) => (
          <div
            onKeyDown={(e) => handleKeyDown(e, closePopover)}
            tabIndex={0}
            className="outline-none">
            <div className="flex flex-col gap-4 p-5">
              {showDateSelector && (
                <DateSelector
                  isDate={isDate}
                  isDateRange={isDateRange}
                  isDateOnly={isDateOnly}
                  value={dateValue}
                  onChange={handleDateChange}
                />
              )}
              <Calendar
                value={calendarValue}
                onChange={handleCalendarChange}
                isRange={isDateRange}
                format={displayFormat}
                minDate={minDate}
                maxDate={maxDate}
              />
              {showTimeSelector && (
                <TimeSelector
                  isTime={isTime}
                  isTimeRange={isTimeRange}
                  isDateRange={isDateRange}
                  value={timeValue}
                  onChange={handleTimeChange}
                  baseDate={dateValue}
                  showRightComponent={showTimeRightComponent}
                />
              )}
              {rangeError && (
                <p role="alert" className="text-sm text-danger-500">
                  {rangeError}
                </p>
              )}
            </div>
            <Footer
              onCancel={() => onCancel(closePopover)}
              onApply={() => onApply(closePopover)}
              isValid={isValid}
            />
          </div>
        )}
      </Popover>
      {error && <p className="pt-2 text-sm text-danger-500">{error}</p>}
    </div>
  );
};

export default Datepicker;
